// src/contexts/UserContext.jsx

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { authService } from '../services/auth';
import { apiService } from '@/services/api';
import { purgeOfflineData } from '@/utils/logoutCleanup';
import { isOfflineModeEnabled, withBootTimeout, restoreOfflineSession, lockIfOfflineMode } from '@/services/offline';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
    logout
  } = useAuth0();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;
  const LAST_ACTIVITY_KEY = 'erx_last_activity';
  const lastActivityRef = useRef(
    parseInt(localStorage.getItem(LAST_ACTIVITY_KEY), 10) || Date.now()
  );
  const wasAuthenticatedRef = useRef(false);

  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  }, []);

  const initRetryRef = useRef(null);

  // Re-auth via redirect: preserves the Auth0 SSO cookie so recovery is
  // usually a silent round-trip, unlike logout() which forces full re-entry.
  // The return path goes through sessionStorage because Auth0Provider mounts
  // outside the router — PostLoginRedirect restores it after the round-trip.
  const reAuthenticate = useCallback(() => {
    authService.clearToken();
    sessionStorage.setItem('post_login_return_to', window.location.pathname + window.location.search);
    loginWithRedirect();
  }, [loginWithRedirect]);

  // Expose the Auth0 token functions to the non-React auth service so the
  // API client can silently refresh the Xano token on 401
  useEffect(() => {
    authService.registerAuth0Helpers(getAccessTokenSilently, getIdTokenClaims);
  }, [getAccessTokenSilently, getIdTokenClaims]);

  // Silent refresh became impossible (refresh token revoked/expired) — send
  // the user through interactive re-auth instead of leaving a dead session
  useEffect(() => {
    const handleSessionExpired = () => reAuthenticate();
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [reAuthenticate]);

  // Only a definitive auth rejection warrants tearing the session down —
  // transient failures (Xano 5xx, parse hiccups) keep the session alive.
  // 404 = token authenticated but no user row (auth/me "Profile not found").
  const isTerminalAuthError = (error) => {
    const terminalCodes = ['missing_refresh_token', 'login_required', 'invalid_grant', 'consent_required'];
    return (
      error?.status === 401 ||
      error?.status === 403 ||
      error?.status === 404 ||
      terminalCodes.includes(error?.error)
    );
  };

  // Transient failure with no user loaded: retry shortly, otherwise the
  // user would sit on a spinner with nothing scheduled to recover
  const scheduleInitRetry = () => {
    if (initRetryRef.current) return;
    initRetryRef.current = setTimeout(() => {
      initRetryRef.current = null;
      initializeUser();
    }, 10000);
  };

  // Shared failure handling for every profile-load path: surface the error,
  // tear down on a terminal auth failure, otherwise schedule a retry.
  const handleInitFailure = (error) => {
    setError(error?.message || 'Failed to load user profile');
    if (isTerminalAuthError(error)) {
      setUser(null);
      purgeOfflineData();
      reAuthenticate();
      return;
    }
    scheduleInitRetry();
  };

  const initializeUser = async () => {
    try {
      setLoading(true);
      setError(null);

      // First attempt: validate/refresh tokens. withBootTimeout is a pure
      // passthrough unless offline mode is enabled, where it bounds a HUNG
      // /auth/me (no fetch timeout exists) so the boot can fall back to the
      // offline snapshot instead of spinning forever.
      const tokenStatus = await withBootTimeout(() => authService.validateTokens(
        getAccessTokenSilently,
        getIdTokenClaims
      ));

      if (tokenStatus.valid && tokenStatus.profile) {
        setUser(tokenStatus.profile);
        return;
      }

      // Token is valid but no profile returned (within validation cache window)
      if (tokenStatus.valid && !tokenStatus.profile) {
        const profile = await authService.getUserProfile();
        setUser(profile);
        return;
      }

      // Network error: keep existing state, will retry on next interval.
      // Offline mode: hydrate from the boot snapshot so a cold offline load
      // reaches the dashboard (or renders the reconnect wall) instead of
      // spinning with no user and nothing scheduled.
      if (tokenStatus.retryable) {
        if (isOfflineModeEnabled() && !user) {
          const restored = await restoreOfflineSession();
          if (restored) setUser(restored.profile);
        }
        return;
      }

      // Second attempt: if we're authenticated but tokens invalid, try fresh exchange
      if (!tokenStatus.valid && isAuthenticated) {
        try {
          const accessToken = await getAccessTokenSilently();
          const idTokenClaims = await getIdTokenClaims();

          if (!idTokenClaims?.__raw) {
            throw new Error('Failed to get ID token claims');
          }

          await authService.exchangeAuth0Tokens(accessToken, idTokenClaims.__raw);
          const profile = await authService.getUserProfile();
          setUser(profile);
        } catch (tokenError) {
          console.error('[UserContext] Token exchange failed:', tokenError);
          throw tokenError;
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('[UserContext] User initialization error:', error);
      handleInitFailure(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  
    const handleAuthChange = async () => {
      // Only proceed if Auth0 has finished loading
      if (auth0Loading) {
        return;
      }
      
      // Check if we're in a callback flow (URL contains code and state params)
      const url = new URL(window.location.href);
      const isInCallbackFlow = url.searchParams.has('code') && url.searchParams.has('state');
      
      // If we're in a callback flow with auth but no user, force user initialization
      if (isInCallbackFlow && isAuthenticated && !user) {
        setLoading(true);
        
        try {
          // The redirect just delivered fresh tokens — use the SDK cache
          const accessToken = await getAccessTokenSilently();
          const idTokenClaims = await getIdTokenClaims();
          
          if (accessToken && idTokenClaims?.__raw) {
            await authService.exchangeAuth0Tokens(accessToken, idTokenClaims.__raw);
            const profile = await authService.getUserProfile();
            setUser(profile);
            updateLastActivity();
          }
        } catch (error) {
          console.error('[UserContext] Failed to initialize user in callback:', error);
          handleInitFailure(error);
        } finally {
          setLoading(false);
        }
        return;
      }

      if (isInCallbackFlow) {
        setLoading(true);

        try {
          // The redirect just delivered fresh tokens — use the SDK cache
          const accessToken = await getAccessTokenSilently();

          // If we got a token, use it to initialize the user
          if (accessToken) {
            const idTokenClaims = await getIdTokenClaims();
            if (idTokenClaims?.__raw) {
              await authService.exchangeAuth0Tokens(accessToken, idTokenClaims.__raw);
              const profile = await authService.getUserProfile();
              setUser(profile);
              updateLastActivity();
            }
          }
        } catch (error) {
          console.error('[UserContext] Failed to restore auth in callback flow:', error);
          handleInitFailure(error);
        } finally {
          setLoading(false);
        }
        return;
      }
      
      // Set loading to true when starting auth checks
      setLoading(true);

      try {
        // If authenticated but no user, initialize
        if (isAuthenticated && !user) {
          // Check inactivity before restoring a non-callback session
          const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY), 10);
          if (lastActivity && (Date.now() - lastActivity) > INACTIVITY_TIMEOUT_MS) {
            // Offline mode: LOCK instead of logging out — the Auth0 redirect
            // is fatal offline and the purge would destroy unsynced work.
            // Falls through to initializeUser so the session mounts behind
            // the lock. No-op (returns false) when the flag is off.
            if (!lockIfOfflineMode('inactivity')) {
              authService.clearToken();
              localStorage.removeItem(LAST_ACTIVITY_KEY);
              purgeOfflineData();
              logout({ logoutParams: { returnTo: window.location.origin } });
              return;
            }
          }

          wasAuthenticatedRef.current = true;
          await initializeUser();
          updateLastActivity();
        }
        // If not authenticated, clear user and token, but only if we're not in a callback
        else if (!isAuthenticated) {
          if (wasAuthenticatedRef.current) {
            await purgeOfflineData();
            wasAuthenticatedRef.current = false;
          }
          setUser(null);
          authService.clearToken();
        }
      } catch (error) {
        console.error('[UserContext] Auth change error:', error);
        handleInitFailure(error);
      } finally {
        // Always finish by setting loading to false
        setLoading(false);
      }
    };
  
    handleAuthChange();
  }, [isAuthenticated, auth0Loading]);

  // Compute supervision summary when user loads — used for nav visibility
  useEffect(() => {
    if (!user?.id || !user?.is_provider) return;

    const computeSupervisionSummary = async () => {
      try {
        const [relationships, assignedNotes] = await Promise.all([
          apiService.getSupervisionRelationships({
            supervisor_id: user.id,
            is_active: true,
          }),
          apiService.getAssignedNotes().catch(() => null),
        ]);

        const assignedNoteList = assignedNotes?.items || assignedNotes || [];
        const summary = {
          isSupervisor: relationships?.length > 0,
          hasAssignedNotes: assignedNoteList.length > 0,
          // Drives the Supervision Queue nav badge (no extra request: the list
          // is already fetched here)
          assignedNoteCount: assignedNoteList.length,
          hasNPSupervisees: (relationships || []).some(r =>
            (r.supervisee_provider?.provider_designation || r._supervisee?.provider_designation) === 'NP'
          ),
          hasAssistantSupervisees: (relationships || []).some(r =>
            ['PTA', 'OTA', 'SLPA'].includes(r.supervisee_provider?.provider_designation || r._supervisee?.provider_designation)
          ),
          isNP: user.provider_designation === 'NP',
          isPhysician: ['MD', 'DO'].includes(user.provider_designation),
        };

        setUser(prev => prev ? { ...prev, supervisionSummary: summary } : prev);
      } catch (err) {
        console.error('[UserContext] Failed to compute supervision summary:', err);
      }
    };

    computeSupervisionSummary();
  }, [user?.id, user?.is_provider]);

  // Token refresh interval + inactivity check
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const checkTokens = async () => {
      const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY), 10) || lastActivityRef.current;
      const inactiveDuration = Date.now() - lastActivity;
      if (inactiveDuration > INACTIVITY_TIMEOUT_MS) {
        // Offline mode: lock, keep the queue and session intact.
        if (lockIfOfflineMode('inactivity')) return;
        authService.clearToken();
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        purgeOfflineData();
        if (!navigator.onLine) {
          window.location.href = '/';
        } else {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
        return;
      }

      try {
        // Proactive renewal: Xano tokens hard-expire 24h after mint — refresh
        // at 23h so the expiry never lands mid-documentation
        const tokenAge = authService.getTokenAgeMs();
        if (tokenAge !== null && tokenAge > 23 * 60 * 60 * 1000) {
          try {
            await authService.refreshXanoToken();
          } catch (renewError) {
            console.error('[UserContext] Proactive token renewal failed:', renewError);
          }
        }

        const tokenStatus = await authService.validateTokens(
          getAccessTokenSilently,
          getIdTokenClaims
        );

        if (!tokenStatus.valid && !tokenStatus.retryable) {
          await initializeUser();
        }
      } catch (error) {
        console.error('[UserContext] Token check failed:', error);
      }
    };

    const interval = setInterval(checkTokens, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user, logout]);

  // Activity tracking for inactivity logout
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    let debounceTimer = null;
    const debouncedActivity = () => {
      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
      }, 5000);
      updateLastActivity();
    };

    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
    const options = { passive: true };
    events.forEach(event => window.addEventListener(event, debouncedActivity, options));

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY), 10) || lastActivityRef.current;
        const elapsed = Date.now() - lastActivity;

        if (elapsed > INACTIVITY_TIMEOUT_MS) {
          // Offline mode: lock, keep the queue and session intact.
          if (lockIfOfflineMode('inactivity')) return;
          authService.clearToken();
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          purgeOfflineData();
          if (!navigator.onLine) {
            window.location.href = '/';
          } else {
            logout({ logoutParams: { returnTo: window.location.origin } });
          }
          return;
        }

        updateLastActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      events.forEach(event => window.removeEventListener(event, debouncedActivity, options));
      document.removeEventListener('visibilitychange', handleVisibility);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [isAuthenticated, user, updateLastActivity]);

  const updateUserProfile = async (updateData) => {
    try {
      if (!user?.id) {
        throw new Error('User not found. Please log in again.');
      }
  
      const updatedProfile = await apiService.updateVelaProvider({
        ...updateData,
        id: user.id
      });
  
      setUser(updatedProfile);
      return updatedProfile;
    } catch (err) {
      console.error('[UserContext] Error updating user profile:', err);
      throw err;
    }
  };

  // Replace the runtime copy of organization.settings after an org settings
  // save, without the full re-init (refreshUser flips the global loading
  // state and remounts the app). Pass the settings object returned by
  // PATCH /organization.
  const patchOrganizationSettings = (settings) => {
    if (!settings) return;
    setUser(prev => (prev?.organization
      ? { ...prev, organization: { ...prev.organization, settings } }
      : prev));
  };

  const value = {
    user,
    loading: loading || auth0Loading,
    error,
    refreshUser: initializeUser,
    isAuthenticated,
    updateUserProfile,
    patchOrganizationSettings,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};